import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { deleteUniversalLicense } from "@dashboard/devices/store";
import { License } from "@dashboard/devices/types";
import { updateProvisioning } from "@dashboard/provisioning/store/index";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning } from "@dashboard/provisioning/types";
import { DialogContentText } from "@mui/material";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

/**
 * The provisionings that hand this universal license code to their devices.
 */
export const getProvisioningsUsingCode = (provisionings: Provisioning[], code: string): Provisioning[] =>
    provisionings.filter((provisioning) => provisioning.settings?.universalLicenses.includes(code));

const withoutCode = (provisioning: Provisioning, code: string): Provisioning => ({
    ...provisioning,
    settings: provisioning.settings && {
        ...provisioning.settings,
        universalLicenses: provisioning.settings.universalLicenses.filter((item) => item !== code),
    },
});

type Props = {
    /**
     * The license to remove the universal status from; the dialog is closed while this is null.
     */
    license: License | null;
    onClose: () => void;
};

/**
 * Asks before removing a license's universal status, then removes its code from every provisioning that uses it as well.
 */
export const RemoveUniversalStatusDialog = ({ license, onClose }: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();
    const provisionings = useSelector(selectProvisionings);

    if (!license) {
        return null;
    }

    const affectedProvisionings = getProvisioningsUsingCode(provisionings, license.activationCode);

    const onConfirm = async () => {
        try {
            await Promise.all(
                affectedProvisionings.map((provisioning) =>
                    dispatch(updateProvisioning(withoutCode(provisioning, license.activationCode))).unwrap(),
                ),
            );
            await dispatch(deleteUniversalLicense(license.id)).unwrap();
            showSuccessToast(t("modals:deleteUniversalLicense.toast.success"));
        } catch (error) {
            showErrorToast((error as Error).message || t("modals:deleteUniversalLicense.toast.error"));
        }
    };

    return (
        <ConfirmDialog
            open
            title={t("modals:deleteUniversalLicense.title")}
            dangerButton
            primaryActionText={t("modals:deleteUniversalLicense.button")}
            secondaryActionText={t("common:button.cancel")}
            onClose={onClose}
            onConfirm={onConfirm}
        >
            <DialogContentText>
                <Trans
                    ns="modals"
                    i18nKey="deleteUniversalLicense.description"
                    count={affectedProvisionings.length}
                    values={{
                        code: license.activationCode,
                        provisionings: affectedProvisionings.map((provisioning) => provisioning.name).join(", "),
                    }}
                    components={{ bold: <strong /> }}
                />
            </DialogContentText>
        </ConfirmDialog>
    );
};
